import { useState, useCallback } from 'react';
import { useApi } from '../../../api/useApi';
import { useProtocol } from '../context';
import { ApiComplementType, API_KEY_MAP, TYPE_OPTIONS, openPdfBlob } from '../shared/labRequestTypes';

export type { ApiComplementType };
export { TYPE_OPTIONS };

export type SelectedType = ApiComplementType | 'all' | null;

export const useLabRequestsList = () => {
  const api = useApi();
  const { protocol } = useProtocol();
  const [selectedType, setSelectedType] = useState<SelectedType>(null);
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<{ type: ApiComplementType; data: any } | null>(null);
  const [limit, setLimit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [printing, setPrinting] = useState(false);

  const loadRequests = useCallback(async (
    type: SelectedType,
    reqLimit: number,
    reqOffset: number
  ) => {
    if (!protocol?.id || !type) return;
    setLoading(true);
    setRequests([]);
    try {
      if (type === 'all') {
        const settled = await Promise.allSettled(
          (Object.entries(API_KEY_MAP) as [ApiComplementType, string][]).map(
            ([complementType, apiKey]) =>
              (api.labRequests[apiKey as keyof typeof api.labRequests] as any)
                .get({ autopsy_protocol: protocol.id, is_sent: true })
                .then((res: any) =>
                  (res.data.results ?? []).map((item: any) => ({
                    ...item,
                    _complementType: complementType
                  }))
                )
                .catch(() => [])
          )
        );
        const merged = (settled as PromiseFulfilledResult<any[]>[])
          .flatMap((r) => (r.status === 'fulfilled' ? r.value : []));
        merged.sort(
          (a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime()
        );
        setRequests(merged);
        setCount(0);
      } else {
        const apiKey = API_KEY_MAP[type];
        const res = await (api.labRequests[apiKey as keyof typeof api.labRequests] as any).get({
          autopsy_protocol: protocol.id,
          is_sent: true,
          limit: reqLimit,
          offset: reqOffset
        });
        setRequests(
          (res.data.results ?? []).map((item: any) => ({ ...item, _complementType: type }))
        );
        setCount(res.data.count ?? 0);
      }
    } catch {
      setRequests([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [api, protocol?.id]);

  const handleTypeChange = useCallback((type: SelectedType) => {
    setSelectedType(type);
    setSelectedRequest(null);
    setOffset(0);
    setPage(1);
    setCount(0);
    if (type) {
      loadRequests(type, limit, 0);
    } else {
      setRequests([]);
    }
  }, [loadRequests, limit]);

  const handlePagination = useCallback((newOffset: number, newPage: number) => {
    setOffset(newOffset);
    setPage(newPage);
    if (selectedType && selectedType !== 'all') {
      loadRequests(selectedType, limit, newOffset);
    }
  }, [loadRequests, selectedType, limit]);

  const handleLimitChange = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setOffset(0);
    setPage(1);
    if (selectedType && selectedType !== 'all') {
      loadRequests(selectedType, newLimit, 0);
    }
  }, [loadRequests, selectedType]);

  const handleRowClick = useCallback((item: any) => {
    const type: SelectedType = (item._complementType as ApiComplementType | undefined) ?? selectedType;
    if (!type || type === 'all') return;
    setSelectedRequest({ type: type as ApiComplementType, data: item });
  }, [selectedType]);

  const handlePrint = useCallback(async () => {
    if (!selectedRequest) return;
    const { type, data } = selectedRequest;
    const apiKey = API_KEY_MAP[type];
    try {
      setPrinting(true);
      const response = await (api.labRequests[apiKey as keyof typeof api.labRequests] as any)
        .print(data.id);
      openPdfBlob(response);
    } catch {
      // silent — print errors are rare edge cases
    } finally {
      setPrinting(false);
    }
  }, [api, selectedRequest]);

  return {
    selectedType, requests, loading, selectedRequest,
    limit, offset, page, count, printing,
    handleTypeChange, handleRowClick, handlePagination, handleLimitChange, handlePrint
  };
};
